import { z } from "zod";
// --- Impresoras ---
export const CrearImpresoraSchema = z.object({
    nombre: z.string().min(2, "El nombre debe tener al menos 2 caracteres").max(80),
    tipoConexion: z.enum(["RED_IP", "SISTEMA_LOCAL", "USB_PUENTE"]).optional().default("RED_IP"),
    ip: z.string().optional().default("127.0.0.1"),
    puerto: z.number().int().min(1).max(65535).optional().default(9100),
    activa: z.boolean().optional().default(true),
    esParaCaja: z.boolean().optional().default(false),
    esParaVentaRapida: z.boolean().optional().default(false),
    esParaPrecuenta: z.boolean().optional().default(false),
    imprimirLogo: z.boolean().optional().default(true),
});
export const EditarImpresoraSchema = CrearImpresoraSchema.partial();
// --- Categorías ---
export const CrearCategoriaSchema = z.object({
    nombre: z.string().min(2, "El nombre de la categoría debe tener al menos 2 caracteres").max(80),
    icono: z.string().max(50).optional().nullable(),
    color: z.string().max(20).optional().nullable(),
    imagenUrl: z.string().optional().nullable(),
    orden: z.number().int().min(0).optional().default(0),
    activa: z.boolean().optional().default(true),
    impresoraId: z.string().uuid("ID de impresora inválido").optional().nullable(),
    visiblePublico: z.boolean().optional().default(true),
});
export const EditarCategoriaSchema = CrearCategoriaSchema.partial();
// --- Productos ---
export const CrearProductoSchema = z.object({
    categoriaId: z.string().uuid("ID de categoría requerido"),
    nombre: z.string().min(2, "El nombre del producto debe tener al menos 2 caracteres").max(120),
    descripcion: z.string().max(500).optional().nullable(),
    precio: z.number().positive("El precio debe ser un número positivo"),
    // Precio para domicilio/recoger en tienda; si no se define, esos pedidos
    // cobran el mismo `precio` de siempre.
    precioParaLlevar: z.number().positive("El precio para llevar debe ser un número positivo").optional().nullable(),
    imagenUrl: z.string().optional().nullable(),
    // Impresora específica para este producto (anula la de su categoría). Si
    // queda vacío, sigue usando la de la categoría como siempre.
    impresoraId: z.string().uuid("ID de impresora inválido").optional().nullable(),
    // Opcional a propósito: la mayoría del catálogo ya existe sin código de
    // barras y debe seguir funcionando igual. Cadena vacía se normaliza a
    // null acá mismo -- si no, dos productos sin código terminarían los dos
    // con codigoBarras="" y la restricción UNIQUE de la base de datos los
    // haría chocar entre sí como si fuera un código real duplicado.
    codigoBarras: z
        .string()
        .trim()
        .max(64, "El código de barras es demasiado largo")
        .optional()
        .nullable()
        .transform((v) => (v && v.length > 0 ? v : null)),
    disponible: z.boolean().optional().default(true),
    controlaInventario: z.boolean().optional().default(false),
    stockActual: z.number().int().min(0).optional().default(0),
    stockMinimo: z.number().int().min(0).optional().default(5),
    orden: z.number().int().min(0).optional().default(0),
});
export const EditarProductoSchema = CrearProductoSchema.partial();
export const ToggleDisponibleProductoSchema = z.object({
    disponible: z.boolean(),
});
// Asigna una misma impresora a un grupo de productos elegidos a mano (no
// necesariamente toda una categoría, porque dentro de una misma categoría
// puede haber productos que van a impresoras distintas).
export const AsignarImpresoraMasivoSchema = z.object({
    productoIds: z.array(z.string().uuid()).min(1, "Selecciona al menos un producto"),
    impresoraId: z.string().uuid().nullable(),
});
export const AsignarCategoriaMasivoSchema = z.object({
    productoIds: z.array(z.string().uuid()).min(1, "Selecciona al menos un producto"),
    categoriaId: z.string().uuid("ID de categoría inválido"),
});
// ------------------------------------------------------------------
// Respuestas (lo que el backend REALMENTE devuelve). z.coerce.number()
// en `precio` por lo mismo de siempre: Prisma serializa `Decimal` como
// string (ver PROGRESS.md sección 5b/7). `stockActual`/`stockMinimo`/
// `orden`/`puerto` son `Int` en el schema, así que ya llegan como
// número — pero como el mismo endpoint a veces sí y a veces no incluye
// las relaciones (crear/editar impresora no traen categorías/_count,
// por ejemplo), esos campos van opcionales.
// ------------------------------------------------------------------
const PrecioCoercible = z.coerce.number();
export const ImpresoraRespuestaSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    nombre: z.string(),
    tipoConexion: z.enum(["RED_IP", "SISTEMA_LOCAL", "USB_PUENTE"]).optional(),
    ip: z.string(),
    puerto: z.number(),
    activa: z.boolean(),
    esParaCaja: z.boolean().optional(),
    esParaVentaRapida: z.boolean().optional(),
    esParaPrecuenta: z.boolean().optional(),
    imprimirLogo: z.boolean().optional(),
    categorias: z.array(z.object({ id: z.string(), nombre: z.string(), color: z.string().nullable().optional() })).optional(),
    _count: z.object({ categorias: z.number() }).optional(),
});
export const CategoriaRespuestaSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    nombre: z.string(),
    icono: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    imagenUrl: z.string().nullable().optional(),
    // Calculada por el backend: la propia si existe, si no la primera foto de
    // un producto de esa categoría (ver CategoriasService.listar). Evita que el
    // frontend tenga que traer el catálogo completo solo para mostrar miniaturas.
    imagenUrlEfectiva: z.string().nullable().optional(),
    orden: z.number(),
    activa: z.boolean(),
    impresoraId: z.string().nullable().optional(),
    impresora: ImpresoraRespuestaSchema.nullable().optional(),
    visiblePublico: z.boolean().optional(),
    _count: z.object({ productos: z.number() }).optional(),
});
export const ProductoRespuestaSchema = z.object({
    id: z.string(),
    sucursalId: z.string(),
    categoriaId: z.string(),
    nombre: z.string(),
    descripcion: z.string().nullable().optional(),
    precio: PrecioCoercible,
    precioParaLlevar: PrecioCoercible.nullable().optional(),
    imagenUrl: z.string().nullable().optional(),
    // URL binaria cacheable a la foto (ver GET /productos/:id/imagen o, en
    // modo "liviano" para listas que no la necesitan editable, esta es la
    // única que trae la foto -- `imagenUrl` llega en null). Opcional porque
    // endpoints viejos/otros consumidores pueden no mandarla.
    imagenUrlEfectiva: z.string().nullable().optional(),
    codigoBarras: z.string().nullable().optional(),
    impresoraId: z.string().nullable().optional(),
    impresora: ImpresoraRespuestaSchema.nullable().optional(),
    disponible: z.boolean(),
    controlaInventario: z.boolean().optional(),
    stockActual: z.number().optional(),
    stockMinimo: z.number().optional(),
    orden: z.number(),
    categoria: CategoriaRespuestaSchema,
});
// --- Catálogo público (tienda en línea / QR de mesa, sin autenticación) ---
export const ProductoPublicoSchema = z.object({
    id: z.string(),
    nombre: z.string(),
    descripcion: z.string().nullable(),
    precio: PrecioCoercible,
    precioParaLlevar: PrecioCoercible.nullable(),
    imagenUrl: z.string().nullable(),
    imagenUrlEfectiva: z.string().nullable().optional(),
    disponible: z.boolean(),
});
export const CategoriaPublicaSchema = z.object({
    id: z.string(),
    nombre: z.string(),
    icono: z.string().nullable(),
    color: z.string().nullable(),
    imagenUrl: z.string().nullable().optional(),
    // Calculada por el backend igual que en el panel admin: la propia si
    // existe, si no la primera foto de un producto de esa categoría. En esta
    // respuesta masiva los `productos[].imagenUrl` individuales llegan en
    // null (se piden aparte por categoría) para no traer TODAS las fotos del
    // catálogo de una sola vez.
    imagenUrlEfectiva: z.string().nullable().optional(),
    orden: z.number(),
    // Cuántos productos disponibles tiene — para el contador de la tarjeta de
    // categoría, sin tener que traer los productos completos (ver comentario
    // en `productos` abajo).
    cantidadProductos: z.number().optional(),
    // Vacío en esta respuesta masiva: los productos completos (con foto) de
    // cada categoría se piden aparte, solo al abrirla (GET /catalogo/publico/
    // categoria) o al buscar (GET /catalogo/publico/buscar).
    productos: z.array(ProductoPublicoSchema),
});
export const ProductosCategoriaPublicaRespuestaSchema = z.object({
    productos: z.array(ProductoPublicoSchema),
});
export const ProductoPublicoBusquedaSchema = ProductoPublicoSchema.extend({
    categoriaId: z.string(),
    categoriaNombre: z.string(),
    categoriaIcono: z.string().nullable(),
    categoriaColor: z.string().nullable(),
});
export const BuscarProductosPublicoRespuestaSchema = z.object({
    productos: z.array(ProductoPublicoBusquedaSchema),
});
export const CatalogoPublicoRespuestaSchema = z.object({
    sucursal: z
        .object({
        id: z.string(),
        nombre: z.string(),
        direccion: z.string().nullable(),
        telefono: z.string().nullable(),
        logoUrl: z.string().nullable().optional(),
        iconoUrl: z.string().nullable().optional(),
        eslogan: z.string().nullable().optional(),
        mostrarNombreLogo: z.boolean().optional(),
        mostrarEsloganLogo: z.boolean().optional(),
        alturaLogoPx: z.coerce.number().optional(),
        anchuraLogoPx: z.coerce.number().optional(),
    })
        .nullable(),
    categorias: z.array(CategoriaPublicaSchema),
});
//# sourceMappingURL=catalogo.schema.js.map